import { useEffect, useRef } from 'react';
import { commitLocalUpdate, useRelayEnvironment } from 'react-relay';
import type { hierarchy_childRowsPreloadedQuery$variables } from '@atlassian/jira-relay/src/__generated__/hierarchy_childRowsPreloadedQuery.graphql';
import RelayDataID from '@atlassian/relay-data-id';
import { ACTION_COLUMNS, MAX_COLUMNS, CHILD_QUERY_PAGE_SIZE } from '../../common/constants.tsx';
import type { Column, SortOrder } from '../../common/types.tsx';
import { getProjectKeyFromIssueKey } from '../../common/utils/index.tsx';
import type { ConnectionMetadata } from '../../controllers/connections-list/index.tsx';

type BaseProps = {
	cloudId: string;
	columns: Column[];
	isReparentingEnabled: boolean;
};

type ChildQueryForJqlProps = BaseProps & {
	jql: string;
};

export const getChildQueryVariablesBase = ({
	cloudId,
	columns,
	isReparentingEnabled,
}: BaseProps) => {
	const fieldSetIds = columns
		.filter(({ id }) => !!id && !ACTION_COLUMNS.includes(id))
		.map(({ id }) => id)
		.sort();

	return {
		cloudId,
		amountOfColumns: MAX_COLUMNS,
		fieldSetsInput: {
			fieldSetIds,
		},
		fieldSetIds,
		first: CHILD_QUERY_PAGE_SIZE,
		namespace: '',
		shouldQueryFieldSetsById: true,
		filterId: null,
		viewId: null,
		isReparentingEnabled,
	};
};

export const getChildQueryVariablesForJql = ({
	cloudId,
	columns,
	jql,
	isReparentingEnabled,
}: ChildQueryForJqlProps) => {
	return {
		...getChildQueryVariablesBase({ cloudId, columns, isReparentingEnabled }),
		issueSearchInput: { jql },
		shouldQueryHasChildren: false,
	};
};

type ChildQueryForParentIssueProps = BaseProps & {
	parentIssueKey: string;
	sortField?: string;
	sortDirection?: SortOrder;
	jql?: string;
	isReparentingEnabled: boolean;
};

export const getChildQueryVariablesForParentIssue = ({
	cloudId,
	columns,
	parentIssueKey,
	jql,
	isReparentingEnabled,
}: ChildQueryForParentIssueProps): hierarchy_childRowsPreloadedQuery$variables => {
	const projectKey = getProjectKeyFromIssueKey(parentIssueKey);

	return {
		...getChildQueryVariablesBase({ cloudId, columns, isReparentingEnabled }),
		issueSearchInput: {
			childIssuesInput: {
				parentIssueKey,
				filterByProjectKeys: [projectKey],
				...(jql
					? {
							jql,
						}
					: {}),
			},
		},
		projectKeys: [projectKey],
		projectKey,
		viewConfigInput: { staticViewInput: { isHierarchyEnabled: true, isGroupingEnabled: false } },
		shouldQueryHasChildren: true,
	};
};

export const useUpdateHierarchyHasChildrenOnExpand = ({
	connectionMetadata,
	projectKey,
	childCount,
}: {
	connectionMetadata: ConnectionMetadata | undefined;
	childCount: number;
	projectKey?: string;
}) => {
	const environment = useRelayEnvironment();

	useEffect(() => {
		if (connectionMetadata?.type === 'PARENT_CHILDREN' && childCount === 0) {
			commitLocalUpdate(environment, (store) => {
				const parentIssueRecord = store.get(connectionMetadata.parentRelayId);
				const updateArgs = projectKey ? { filterByProjectKeys: [projectKey] } : undefined;
				if (parentIssueRecord && parentIssueRecord.getValue('hasChildren', updateArgs) === true) {
					parentIssueRecord.setValue(false, 'hasChildren', updateArgs);
				}
			});
		}
	}, [childCount, connectionMetadata, environment, projectKey]);
};

export const useUpdateGroupIssueCountOnExpand = ({
	groupId,
	isGroupChildren,
	totalIssueSearchResultCount,
	isCappingIssueSearchResult,
}: {
	groupId: string | undefined;
	isGroupChildren: boolean;
	totalIssueSearchResultCount: number;
	isCappingIssueSearchResult: boolean;
}) => {
	const environment = useRelayEnvironment();
	// we need to update the group only once when the component mounts which happens on first expand
	// Other changes to issue count will be handled by realtime connection updates
	// also, we don't need to update the "firstGroup" field in the group record as it's expanded on default and realtime event always updates it
	const hasIssueCountUpdated = useRef(false);

	useEffect(() => {
		if (!hasIssueCountUpdated.current && groupId && isGroupChildren) {
			commitLocalUpdate(environment, (store) => {
				const relayId = RelayDataID({ id: groupId }, 'JiraSpreadsheetGroup');

				if (!relayId) {
					return;
				}

				const groupRecord = store.get(relayId);
				if (
					groupRecord?.getValue('issueCount') !== totalIssueSearchResultCount &&
					!isCappingIssueSearchResult
				) {
					groupRecord?.setValue(totalIssueSearchResultCount, 'issueCount');
				}
			});
		}
		hasIssueCountUpdated.current = true;
	}, [
		environment,
		groupId,
		isCappingIssueSearchResult,
		isGroupChildren,
		totalIssueSearchResultCount,
	]);
};
